/**
 * Text-scramble decryption effect.
 * Adapted from Justin Windle's classic CodePen pattern (soulwire/mEMPrK)
 * and re-typed for our purposes.
 *
 * Keeps a per-char tween: {from, to, start, end, char} and resolves over time.
 */

const CHARS = '!<>-_\\/[]{}—=+*^?#________ABCDEF0123456789';

type ScrambleQueue = {
  from: string;
  to: string;
  start: number;
  end: number;
  char: string;
}[];

export class TextScramble {
  private queue: ScrambleQueue = [];
  private frame = 0;
  private rafId: number | null = null;
  private resolver: (() => void) | null = null;

  constructor(private el: HTMLElement) {}

  /** Animate from the element's current text → `target`. Returns a Promise. */
  setText(target: string, durationFrames = 36): Promise<void> {
    const from = this.el.textContent ?? '';
    const length = Math.max(from.length, target.length);
    this.queue = [];
    for (let i = 0; i < length; i++) {
      const fromCh = from[i] ?? '';
      const toCh = target[i] ?? '';
      const start = Math.floor(Math.random() * (durationFrames * 0.4));
      const end = start + Math.floor(Math.random() * (durationFrames * 0.6)) + 4;
      this.queue.push({ from: fromCh, to: toCh, start, end, char: '' });
    }
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.frame = 0;
    return new Promise<void>((resolve) => {
      this.resolver = resolve;
      this.update();
    });
  }

  private update = () => {
    let output = '';
    let complete = 0;
    for (let i = 0; i < this.queue.length; i++) {
      const q = this.queue[i];
      if (this.frame >= q.end) {
        complete++;
        output += q.to;
      } else if (this.frame >= q.start) {
        if (!q.char || Math.random() < 0.28) {
          q.char = CHARS[Math.floor(Math.random() * CHARS.length)];
        }
        output += `<span style="color:#7ce4ff;text-shadow:0 0 6px rgba(124,228,255,0.5)">${escapeHtml(q.char)}</span>`;
      } else {
        output += q.from;
      }
    }
    this.el.innerHTML = output;
    if (complete === this.queue.length) {
      this.rafId = null;
      this.el.textContent = this.queue.map((q) => q.to).join('');
      this.resolver?.();
      this.resolver = null;
    } else {
      this.frame++;
      this.rafId = requestAnimationFrame(this.update);
    }
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

/**
 * Scramble every element in `nodes` to its `data-scramble` attribute,
 * with a small stagger between starts. Resolves when all are done.
 */
export function scrambleAll(
  nodes: NodeListOf<HTMLElement> | HTMLElement[],
  staggerMs = 35,
  durationFrames = 28
): Promise<void> {
  const arr = Array.from(nodes);
  const promises: Promise<void>[] = [];
  arr.forEach((el, i) => {
    const target = el.dataset.scramble ?? el.textContent ?? '';
    const ts = new TextScramble(el);
    el.textContent = ''; // start blank
    promises.push(
      new Promise<void>((resolve) => {
        setTimeout(() => {
          ts.setText(target, durationFrames).then(resolve);
        }, i * staggerMs);
      })
    );
  });
  return Promise.all(promises).then(() => undefined);
}
